/**
 * Maps Firebase Auth error codes to user-friendly English messages.
 * Used across all login forms (prospect, promoter, vendor, superadmin).
 */

const FIREBASE_AUTH_ERROR_MESSAGES: Record<string, string> = {
  // Sign-in / credential errors
  'auth/invalid-credential': 'Invalid email or password. Please check your credentials and try again.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/user-disabled': 'This account has been disabled. Please contact support.',
  'auth/user-not-found': 'No account found with this email address.',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
  'auth/network-request-failed': 'Network error. Please check your connection and try again.',
  'auth/operation-not-allowed': 'This sign-in method is not enabled. Please contact support.',
  'auth/requires-recent-login': 'Please sign in again to complete this action.',
  'auth/app-not-authorized': 'This app is not authorized. Please contact support.',
  'auth/invalid-api-key': 'Authentication configuration error. Please contact support.',
  'auth/internal-error': 'An unexpected error occurred. Please try again.',

  // Registration errors
  'auth/email-already-in-use': 'This email is already registered.',
  'auth/weak-password': 'Password is too weak. Please use at least 6 characters.',
  'auth/invalid-password': 'Password must be at least 6 characters.',

  // Password reset / action code errors
  'auth/expired-action-code': 'This link has expired. Please request a new one.',
  'auth/invalid-action-code': 'This link is invalid or has already been used.',
  'auth/code-expired': 'This code has expired. Please request a new one.',
  'auth/invalid-continue-uri': 'Invalid reset link. Please request a new password reset.',

  // Custom token errors
  'auth/custom-token-mismatch': 'Invalid authentication token. Please try signing in again.',
  'auth/invalid-custom-token': 'Invalid authentication token. Please try signing in again.',

  // Multi-factor / phone auth (for completeness)
  'auth/multi-factor-auth-required': 'Additional verification is required to sign in.',
  'auth/missing-phone-number': 'Please provide a valid phone number.',
  'auth/invalid-phone-number': 'Please enter a valid phone number.',
  'auth/invalid-verification-code': 'Invalid verification code. Please try again.',
  'auth/captcha-check-failed': 'Security verification failed. Please try again.',
  'auth/quota-exceeded': 'Too many requests. Please try again later.',
};

/**
 * Returns a user-friendly English message for Firebase Auth errors.
 * Hides raw Firebase error codes (e.g. "auth/invalid-credential") from users.
 */
export function getFirebaseAuthErrorMessage(error: unknown): string {
  const fallback = 'An unexpected error occurred. Please try again.';

  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const err = error as { code?: string; message?: string };
  const code = err.code;

  if (code && typeof code === 'string' && FIREBASE_AUTH_ERROR_MESSAGES[code]) {
    return FIREBASE_AUTH_ERROR_MESSAGES[code];
  }

  // Fallback: if message contains "auth/", avoid exposing it
  const message = err.message;
  if (message && typeof message === 'string' && message.includes('auth/')) {
    return fallback;
  }

  return message || fallback;
}
